import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Clock,
  Megaphone,
  Sparkles,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
} from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { GOOD_ATTENDANCE_PERCENT } from '@/constants/attendance';
import { gradeForPaper } from '@/constants/grading';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { WEEKDAY_LABEL, periodRange } from '@/features/teacher/components/timetable/timetableCopy';
import { formatInstituteDate } from '@/features/teacher/utils';
import { formatTimeRange } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { StudentAnnouncement, StudentOverview, StudentProfile } from '@/types/student';
import { AnimatedInstituteHero } from './AnimatedInstituteHero';
import { StudentNoticeCard } from './StudentNoticeCard';
import { TopStudentsSection } from './TopStudentsSection';

/**
 * The first screen of the student panel.
 *
 * Every figure on it comes from `/student/overview`, which answers all of them
 * about the same child on the same day — so the greeting, the cards and the
 * schedule below them cannot disagree with each other or with the tabs beside
 * them.
 *
 * The child's own identity comes from `/student/me` instead, as the sidebar and
 * every other tab's title do: one source for who they are, one for how they are
 * doing.
 */

interface AcademicOverviewProps {
  profile: StudentProfile | null;
  overview: StudentOverview | null;
  isLoading: boolean;
  error: { message: string } | null;
  onRetry: () => void;
  /** The child's own Settings choice — off hides who takes each lesson. */
  showTeacherName?: boolean;
}

/**
 * What an attendance figure is worth, said in words.
 *
 * Read top down, first band cleared wins — the same shape `constants/grading`
 * uses for marks. Below the shared good-attendance bar the card stops
 * congratulating and points at a teacher, so it cannot praise a figure the
 * profile tab treats as a warning.
 */
function attendanceStatusOf(percentage: number | null) {
  if (percentage === null) {
    return {
      label: 'Overall Attendance',
      badge: 'No register taken yet',
      colorClass: 'text-ink-2',
      bgClass: 'bg-surface-2 border-line',
      pillBg: 'bg-surface text-ink-2 border-line',
      sparkleColor: 'text-ink-3',
      icon: <CheckCircle2 className="h-4 w-4 text-ink-3" />,
    };
  }

  if (percentage >= 85) {
    return {
      label: 'Overall Attendance',
      badge: 'Great Job! Keep it up 🌟',
      colorClass: 'text-[#159a68]',
      bgClass: 'bg-[#f0fbf7] border-[#cceade]',
      pillBg: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      sparkleColor: 'text-emerald-500',
      icon: <CheckCircle2 className="h-4 w-4 text-[#159a68]" />,
    };
  }

  if (percentage >= GOOD_ATTENDANCE_PERCENT) {
    return {
      label: 'Good Progress',
      badge: 'Good Effort · Attend Regularly 👍',
      colorClass: 'text-[#d97706]',
      bgClass: 'bg-[#fffbf0] border-[#fde68a]',
      pillBg: 'bg-amber-100 text-amber-800 border-amber-300',
      sparkleColor: 'text-amber-500',
      icon: <TrendingUp className="h-4 w-4 text-[#d97706]" />,
    };
  }

  return {
    label: 'Needs Attention',
    badge: 'Requires Focus · Consult Teacher 🎯',
    colorClass: 'text-[#dc2626]',
    bgClass: 'bg-[#fff2f2] border-[#fecaca]',
    pillBg: 'bg-red-100 text-red-800 border-red-300',
    sparkleColor: 'text-red-500',
    icon: <AlertTriangle className="h-4 w-4 text-[#dc2626]" />,
  };
}

/** How a notice is drawn — a closure reads differently from a paper to sit. */
const ANNOUNCEMENT_STYLE: Record<
  StudentAnnouncement['kind'],
  { wrapper: string; title: string; image: string; imageClass: string }
> = {
  HOLIDAY: {
    wrapper: 'bg-crit-bg border-crit/20',
    title: 'text-crit',
    image: '/student-overview/flag.png',
    imageClass: 'h-[70px] w-[70px]',
  },
  EXAM: {
    wrapper: 'bg-info-bg border-info/20',
    title: 'text-info',
    image: '/student-overview/calendar.png',
    imageClass: 'h-[72px] w-[72px] object-cover',
  },
};

/** The three pastel rows the schedule alternates between, cycled down the day. */
const PERIOD_TONES = [
  {
    row: 'bg-[#eff9f4] dark:bg-emerald-950/30 dark:border dark:border-emerald-500/20',
    badge: 'bg-[#b7ebd0] text-[#15975f] dark:bg-emerald-900/60 dark:text-emerald-300 dark:border-emerald-500/30',
    accent: 'text-[#15975f] dark:text-emerald-400',
  },
  {
    row: 'bg-[#fff2f6] dark:bg-pink-950/30 dark:border dark:border-pink-500/20',
    badge: 'bg-[#ffc4da] text-[#e23772] dark:bg-pink-900/60 dark:text-pink-300 dark:border-pink-500/30',
    accent: 'text-[#e23772] dark:text-pink-400',
  },
  {
    row: 'bg-[#f4efff] dark:bg-purple-950/30 dark:border dark:border-purple-500/20',
    badge: 'bg-[#ddceff] text-[#7a42d9] dark:bg-purple-900/60 dark:text-purple-300 dark:border-purple-500/30',
    accent: 'text-[#7a42d9] dark:text-purple-400',
  },
];

export function AcademicOverview({
  profile,
  overview,
  isLoading,
  error,
  onRetry,
  showTeacherName = true,
}: AcademicOverviewProps) {
  const navigate = useNavigate();
  // A school without the calendar has nowhere to send "view more" on the
  // announcements, so the link is left off rather than opening a locked section.
  const hasCalendar = useAuthStore((state) => state.user?.features?.includes('CALENDAR') ?? false);
  const attendance = attendanceStatusOf(overview?.attendance.percentage ?? null);
  const recentMark = overview?.recentMark ?? null;
  // Held to the paper's own pass mark, so a failed paper never wears a passing grade.
  const grade = recentMark
    ? gradeForPaper(recentMark.marksObtained, recentMark.maxMarks, recentMark.passMarks)
    : null;
  const today = overview?.today ?? null;
  const todayExams = today?.exams ?? [];
  const isDayEmpty = today !== null && today.periods.length === 0 && todayExams.length === 0;

  return (
    <div className="flex flex-col gap-6 pb-1 animate-fade-in">
      {/* Welcome Hero Component */}
      <AnimatedInstituteHero profile={profile} overview={overview} />

      {error && (
        <div className="flex flex-col items-start gap-3">
          <Alert tone="critical" title="Could not load your dashboard">
            {error.message}
          </Alert>
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}

      {isLoading && !error && (
        <div className="rounded-[20px] border border-[#e0e2ef] bg-white p-5 shadow-[0_8px_20px_rgba(40,42,85,.07)] dark:border-white/10 dark:bg-[#1b1d34]">
          <SkeletonLines count={10} />
        </div>
      )}

      {!isLoading && !error && overview && (
        <>
          <div className="grid grid-cols-1 gap-3.5 sm:gap-4 sm:grid-cols-3">
            {/* Attendance Card */}
            <div
              className={cn(
                'group rounded-[19px] border p-4 sm:p-5 shadow-[0_8px_20px_rgba(24,104,77,.06)] flex items-center justify-between gap-2 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl dark:border-emerald-400/20 dark:bg-[linear-gradient(135deg,#1b2d30,#142526)]',
                attendance.bgClass,
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 mb-1">
                  {attendance.icon}
                  <p
                    className={cn(
                      'text-xs font-extrabold uppercase tracking-wide truncate',
                      attendance.colorClass,
                    )}
                  >
                    {attendance.label}
                  </p>
                </div>
                {/* A dash, never a zero: "nobody has marked you" and "you were
                    never here" are different facts and must not look alike. */}
                <p
                  className={cn(
                    'mt-1 text-[28px] sm:text-[34px] leading-none font-black',
                    attendance.colorClass,
                  )}
                >
                  {overview.attendance.percentage === null
                    ? '—'
                    : `${overview.attendance.percentage}%`}
                </p>
                <span
                  className={cn(
                    'mt-2.5 sm:mt-3 inline-block px-2.5 py-0.5 rounded-pill text-[11px] font-extrabold border shadow-2xs group-hover:scale-105 transition-transform truncate max-w-full',
                    attendance.pillBg,
                  )}
                >
                  {attendance.badge}
                </span>
              </div>

              {/* Animated Gauge Graphic (Dynamically Responds to Percentage) */}
              <div className="relative shrink-0">
                <img
                  src="/student-overview/attendance-illustration.png"
                  alt="Attendance Percentage Gauge"
                  className="h-16 w-16 sm:h-[88px] sm:w-[88px] object-cover transition-transform duration-300 group-hover:scale-115 group-hover:rotate-6 animate-float-slow"
                />
                <Sparkles
                  className={cn(
                    'absolute -top-1 -right-1 h-4 w-4 sm:h-5 sm:w-5 animate-sparkle',
                    attendance.sparkleColor,
                  )}
                />
              </div>
            </div>

            {/* Pending Homework Card */}
            <div className="group rounded-[19px] border border-[#fae0b8] bg-[linear-gradient(135deg,#fffdfa,#fff7e9)] p-4 sm:p-5 shadow-[0_8px_20px_rgba(164,106,8,.06)] flex items-center justify-between gap-2 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl dark:border-amber-300/20 dark:bg-[linear-gradient(135deg,#302719,#251e14)]">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-extrabold uppercase tracking-wide text-[#ea9212] truncate">
                  Pending Homework
                </p>
                <p className="mt-1 sm:mt-2 text-[28px] sm:text-[34px] leading-none font-black text-[#f29a0b]">
                  {overview.homework.pending}{' '}
                  {overview.homework.pending === 1 ? 'Task' : 'Tasks'}
                </p>
                <span className="mt-2.5 sm:mt-3 inline-block px-2.5 py-0.5 rounded-pill text-[11px] font-extrabold bg-amber-100 text-amber-900 border border-amber-300 group-hover:scale-105 transition-transform truncate max-w-full">
                  {overview.homework.dueThisWeek === 0
                    ? 'Nothing due this week 🎉'
                    : `${overview.homework.dueThisWeek} due this week 📝`}
                </span>
              </div>

              {/* Animated Clipboard Graphic */}
              <div className="relative shrink-0">
                <img
                  src="/student-overview/homework-illustration.png"
                  alt="Homework Clipboard"
                  className="h-16 w-16 sm:h-[94px] sm:w-[98px] object-cover transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110 animate-float-slow"
                  style={{ animationDelay: '1s' }}
                />
              </div>
            </div>

            {/* Recent Grade Card */}
            <div className="group rounded-[19px] border border-[#d4e1fb] bg-[linear-gradient(135deg,#fff,#f2f6ff)] p-4 sm:p-5 shadow-[0_8px_20px_rgba(41,100,195,.06)] flex items-center justify-between gap-2 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl dark:border-blue-300/20 dark:bg-[linear-gradient(135deg,#1b263d,#151c2e)]">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-extrabold uppercase tracking-wide text-[#3676dc] truncate">
                  {recentMark ? `Recent ${recentMark.subject} Grade` : 'Recent Grade'}
                </p>
                <p className="mt-1 sm:mt-2 text-[28px] sm:text-[34px] leading-none font-black text-[#3173d9]">
                  {recentMark ? `${recentMark.marksObtained} / ${recentMark.maxMarks}` : '—'}
                </p>
                <span className="mt-2.5 sm:mt-3 inline-block px-2.5 py-0.5 rounded-pill text-[11px] font-extrabold bg-blue-100 text-blue-900 border border-blue-300 group-hover:scale-105 transition-transform truncate max-w-full">
                  {recentMark && grade
                    ? `Grade ${grade.grade} (${recentMark.examName}) 🏆`
                    : 'No papers marked yet'}
                </span>
              </div>

              {/* Animated Trophy Graphic */}
              <div className="relative shrink-0">
                <img
                  src="/student-overview/grade-illustration.png"
                  alt="Recent Grade Trophy"
                  className="h-16 w-16 sm:h-[88px] sm:w-[90px] object-cover transition-transform duration-300 group-hover:scale-115 group-hover:-rotate-6 animate-float-slow animate-gold-shine"
                  style={{ animationDelay: '2s' }}
                />
              </div>
            </div>
          </div>

          {/* Top 3 Students Achievement Showcase Section */}
          <TopStudentsSection leaderboard={overview.leaderboard} />

          {/* Today's Schedule & Announcements */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
            {/* Today's Schedule */}
            <div className="lg:col-span-2 rounded-[20px] border border-[#e0e2ef] bg-white shadow-[0_8px_20px_rgba(40,42,85,.07)] flex flex-col justify-between overflow-hidden dark:border-white/10 dark:bg-[#1b1d34]">
              <div className="px-4 py-4 sm:px-5 sm:pt-5 sm:pb-3">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#e9e9f0] pb-3 sm:pb-4 dark:border-white/10">
                  <h3 className="text-sm sm:text-base font-extrabold text-[#1b2142] dark:text-[#f2f4ff] flex items-center gap-2 sm:gap-3">
                    <span className="grid h-7 w-7 sm:h-8 sm:w-8 place-items-center rounded-full bg-[#dff8f5] text-[#26bdb5] dark:bg-[#26bdb5]/20 shrink-0">
                      <Clock className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
                    </span>{' '}
                    <span>Today's Class Schedule ({today ? WEEKDAY_LABEL[today.weekday] : '—'})</span>
                  </h3>
                  <span className="text-xs text-[#7d8198] font-semibold dark:text-[#a0a5bd]">
                    {today?.periods.length ?? 0} Periods
                    {todayExams.length > 0 &&
                      ` · ${todayExams.length} ${todayExams.length === 1 ? 'Exam' : 'Exams'}`}
                  </span>
                </div>

                <div className="flex flex-col gap-2.5 sm:gap-3 pt-3 sm:pt-4">
                  {isDayEmpty && today && (
                    <p className="py-6 text-center text-sm font-semibold text-[#7d8198] dark:text-[#a0a5bd]">
                      {today.holiday
                        ? `No classes today — the institute is closed for ${today.holiday}.`
                        : !today.isWorkingDay
                          ? 'No classes today — the institute is closed.'
                          : 'Nothing is on your timetable for today yet.'}
                    </p>
                  )}

                  {/* Today's papers first: on an exam day they are the day, and
                      the lessons, if any run at all, come around them. */}
                  {todayExams.map((paper) => (
                    <div
                      key={paper.uuid}
                      className="p-3 sm:p-3.5 rounded-[15px] border border-warn bg-warn-bg flex items-center justify-between gap-2.5 sm:gap-3"
                    >
                      <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
                        <span className="grid h-10 w-10 sm:h-12 sm:w-12 place-items-center rounded-full bg-surface text-warn shadow-sm shrink-0">
                          <ClipboardCheck className="h-5 w-5" aria-hidden />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-extrabold uppercase tracking-wide text-warn">
                            Exam
                          </p>
                          <h4 className="text-[13px] sm:text-[14px] font-extrabold text-ink truncate">
                            {paper.subject.name}
                          </h4>
                          <p className="text-[11px] sm:text-[12px] text-ink-2 font-medium truncate">
                            {paper.name} · {paper.maxMarks} marks
                          </p>
                        </div>
                      </div>

                      <span className="text-[12px] sm:text-[14px] font-extrabold text-ink text-right shrink-0 tabular-nums">
                        {formatTimeRange(paper.startTime, paper.endTime, 'Time not set')}
                      </span>
                    </div>
                  ))}

                  {today?.periods.map((slot, idx) => {
                    // Cycled rather than three fixed rows: a day of eight
                    // periods should keep alternating, not turn purple at the
                    // third.
                    const tone = PERIOD_TONES[idx % PERIOD_TONES.length];

                    return (
                      <div
                        key={slot.periodNumber}
                        className={cn(
                          'p-3 sm:p-3.5 rounded-[15px] flex items-center justify-between gap-2.5 sm:gap-3 transition-all hover:translate-x-1',
                          tone.row,
                        )}
                      >
                        <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
                          <span
                            className={cn(
                              'grid h-10 w-10 sm:h-12 sm:w-12 place-items-center rounded-full border-[2px] sm:border-[3px] border-white dark:border-white/10 text-xs sm:text-sm font-extrabold shadow-sm shrink-0',
                              tone.badge,
                            )}
                          >
                            P{slot.periodNumber}
                          </span>
                          <div className="min-w-0 flex-1">
                            <h4 className="text-[13px] sm:text-[14px] font-extrabold text-[#202035] dark:text-[#f2f4ff] truncate">
                              {slot.subject}
                            </h4>
                            {showTeacherName && (
                              <p className="text-[11px] sm:text-[12px] text-[#626579] dark:text-[#bbc0d3] font-medium truncate">
                                Teacher: {slot.teacher ?? 'Not allotted yet'}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <span className="text-[12px] sm:text-[14px] font-extrabold text-[#202035] dark:text-[#f2f4ff] block">
                            {periodRange(slot.startTime, slot.endTime)}
                          </span>
                          {slot.roomName && (
                            <span className={cn('text-[11px] sm:text-[12px] font-extrabold', tone.accent)}>
                              {slot.roomName}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <img
                src="/student-overview/schedule-scene.png"
                alt=""
                className="mt-auto w-full object-cover object-top"
              />
            </div>

            {/* Institute Notices */}
            <div className="rounded-[20px] border border-[#e0e2ef] bg-white p-5 shadow-[0_8px_20px_rgba(40,42,85,.07)] flex flex-col justify-between overflow-hidden dark:border-white/10 dark:bg-[#1b1d34]">
              <div className="flex items-center justify-between gap-2 border-b border-[#e9e9f0] pb-4 dark:border-white/10">
                <h3 className="text-base font-extrabold text-[#1b2142] dark:text-[#f2f4ff] flex items-center gap-3">
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-[#eee9ff] text-[#7354df]">
                    <Bell className="h-[18px] w-[18px] animate-bounce" />
                  </span>{' '}
                  Class Notices
                </h3>

                {/* What their own teachers have put up, as a number — the
                    notices themselves live in their own section, which is where
                    this goes. Counted by the server under the same rule that
                    section reads by, so the two cannot disagree. */}
                <div className="flex items-center gap-2">
                  <span className="hidden sm:inline-flex items-center gap-1.5 rounded-pill border border-[#7354df]/20 bg-[#eee9ff] px-2.5 py-1 text-[11px] font-extrabold text-[#7354df] dark:bg-[#7354df]/15">
                    <Megaphone className="h-3.5 w-3.5 shrink-0" />
                    {overview.notices.today > 0
                      ? `${overview.notices.today} today · ${overview.notices.total}`
                      : `${overview.notices.total} ${
                          overview.notices.total === 1 ? 'Notice' : 'Notices'
                        }`}
                  </span>
                  {/* <button
                    type="button"
                    onClick={() => navigate('/student/notices')}
                    className="flex shrink-0 items-center gap-1 rounded-pill bg-[#7354df] px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-[#6244cb] cursor-pointer shadow-2xs"
                    title="View all notices"
                  >
                    View all
                    <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                  </button> */}
                </div>
              </div>

              <div className="flex flex-col gap-3 text-xs">
                {/* What their own teachers have put up, in full — the newest few
                    of the number in the pill above. The rest are in the notices
                    section, which the pill opens. */}
                {overview.notices.latest.slice(0, 4).map((notice) => (
                  <StudentNoticeCard key={notice.uuid} notice={notice} variant="compact" />
                ))}

                {overview.notices.latest.length > 0 && (
                  <button
                    type="button"
                    onClick={() => navigate('/student/notices')}
                    className="self-start text-[12px] font-bold text-[#7354df] hover:underline cursor-pointer flex items-center gap-1 pt-1"
                  >
                    View all {overview.notices.total > 0 ? `${overview.notices.total} notices` : 'notices'}
                    <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                  </button>
                )}

                {/* Holidays and exam dates, which the school office sets — not
                    notices, so they get their own heading. The calendar holds
                    the whole session's worth, so that is where "view more" goes. */}
                {overview.announcements.length > 0 && (
                  <div
                    className={cn(
                      'flex items-center justify-between gap-2',
                      overview.notices.latest.length > 0 &&
                        'border-t border-[#e9e9f0] pt-3 dark:border-white/10',
                    )}
                  >
                    <h4 className="text-sm font-extrabold text-[#1b2142] dark:text-[#f2f4ff]">
                      Announcements
                    </h4>
                    {hasCalendar && (
                      <button
                        type="button"
                        onClick={() => navigate('/student/calendar')}
                        className="flex items-center gap-1 text-[12px] font-bold text-[#7354df] hover:underline cursor-pointer"
                      >
                        View more
                        <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                      </button>
                    )}
                  </div>
                )}

                {overview.notices.latest.length === 0 && overview.announcements.length === 0 && (
                  <p className="py-6 text-center text-sm font-semibold text-[#7d8198] dark:text-[#a0a5bd]">
                    Nothing coming up. Notices from your teachers, holidays and exam dates appear
                    here as they are published.
                  </p>
                )}

                {overview.announcements.map((announcement) => {
                  const style = ANNOUNCEMENT_STYLE[announcement.kind];

                  return (
                    <div
                      key={`${announcement.kind}-${announcement.on}-${announcement.title}`}
                      className={cn(
                        ' overflow-hidden p-3 rounded-[12px] border flex flex-row gap-4 transition-all hover:scale-[1.02]',
                        style.wrapper,
                      )}
                    >
                      <img src={style.image} alt="" className={style.imageClass} />
                      <div>
                        <span className={cn('font-extrabold pe-2 text-[14px]', style.title)}>
                          {announcement.title}
                        </span>
                        {announcement.body && (
                          <p className="text-ink-2 pt-1 text-[12px] font-medium">
                            {announcement.body}
                          </p>
                        )}
                        <span className="text-3xs  text-ink-3">
                          {formatInstituteDate(announcement.on)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <img
                src="/student-overview/announcement-books.png"
                alt=""
                className="-mx-5 -mb-5 mt-auto h-20 w-[calc(100%+2.5rem)] rounded-b-[20px] object-contain object-bottom animate-float-slow animate-pulse-slow"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
